/**
 * useNotificationToast – global hook for real-time message alerts.
 *
 * Listens to the Socket.IO `notification_alert` event and:
 *   1. Shows a clickable react-toastify toast inside the app
 *   2. Shows a browser Notification when the tab is hidden (if permitted)
 *
 * Clicking either navigates the user directly to the conversation.
 *
 * Mount this hook ONCE inside <App /> (within Router context).
 */

import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { MessageSquare } from '../icons';
import { onNotificationAlert } from '../services/socket';
import { useAuth } from '../context/AuthContext';
import React from 'react';

/**
 * Request browser notification permission on mount (once).
 */
function useBrowserNotifPermission() {
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }
  }, []);
}

/**
 * Custom toast body – clickable card with sender name + preview.
 */
function MessageToastBody({ senderName, preview, senderType, onClick }) {
  return (
    <div onClick={onClick} style={{ cursor: 'pointer', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
      <div style={{
        width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: senderType === 'vendor' ? '#EFF6FF' : '#F0FDF4',
        color: senderType === 'vendor' ? '#2563EB' : '#16A34A',
      }}>
        <MessageSquare size={18} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: '13px', color: '#1f2937', marginBottom: '2px' }}>
          {senderName}
        </div>
        <div style={{
          fontSize: '12px', color: '#6b7280', lineHeight: 1.4,
          overflow: 'hidden', textOverflow: 'ellipsis',
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
        }}>
          {preview}
        </div>
      </div>
    </div>
  );
}

export default function useNotificationToast() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const activeToastsRef = useRef(new Set());

  useBrowserNotifPermission();

  const handleAlert = useCallback((data) => {
    if (!user) return;

    const { senderName, senderType, preview, link, messageId } = data;

    // Deduplicate – don't show two toasts for the same message
    if (activeToastsRef.current.has(messageId)) return;
    activeToastsRef.current.add(messageId);
    setTimeout(() => activeToastsRef.current.delete(messageId), 10000);

    // Don't notify for my own messages (belt-and-suspenders – server shouldn't send these)
    // The server only emits to the OTHER user's room, but just in case:
    if (senderType === 'vendor' && user.role === 'vendor') return;
    if (senderType === 'couple' && user.role === 'couple') return;

    // The conversation is open on screen already — only a hidden tab needs telling.
    if (data.viewing && !document.hidden) return;

    const goToMessage = () => {
      if (link) {
        // If we're already on the page, force a reload to jump to that inquiry
        if (window.location.pathname + window.location.search === link) {
          window.location.reload();
        } else {
          navigate(link);
        }
      }
    };

    // ─── In-app toast ───
    toast(
      <MessageToastBody
        senderName={senderName}
        preview={preview}
        senderType={senderType}
        onClick={goToMessage}
      />,
      {
        position: 'top-right',
        autoClose: 6000,
        hideProgressBar: false,
        closeOnClick: false, // we handle click ourselves
        pauseOnHover: true,
        draggable: true,
        onClick: goToMessage,
        style: {
          borderLeft: `4px solid ${senderType === 'vendor' ? '#2563EB' : '#16A34A'}`,
          borderRadius: '10px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
        },
      }
    );

    // ─── Browser notification (when tab is hidden) ───
    if ('Notification' in window && Notification.permission === 'granted' && document.hidden) {
      try {
        const notif = new Notification(`New message from ${senderName}`, {
          body: preview,
          icon: '/logo192.png',
          tag: `nepwed-msg-${messageId}`, // deduplicate
          requireInteraction: false,
        });
        notif.onclick = () => {
          window.focus();
          goToMessage();
          notif.close();
        };
        // Auto-close after 8 seconds
        setTimeout(() => notif.close(), 8000);
      } catch { /* Safari can throw in some contexts */ }
    }
  }, [user, navigate]);

  // Subscribe to socket events
  useEffect(() => {
    const unsub = onNotificationAlert(handleAlert);
    return () => unsub();
  }, [handleAlert]);
}
